import { Suspense, memo, startTransition, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { ReactNode, RefObject } from 'react'
import { project, rubberBand, springFrames, velocityOf } from '@lunary/app-core/swipe-physics'
import type { Sample } from '@lunary/app-core/swipe-physics'
import styles from './swipe-pager.module.css'

export type SwipePreview = {
  move: (from: string, to: string, progress: number) => void
  end: (committed: boolean) => void
}

type Peer = { key: string; side: 1 | -1; top: number }

type Gesture = { id: number; x0: number; y0: number; base: number; lock: 'none' | 'x' | 'y'; samples: Sample[]; width: number; offset: number }

type Commit = { to: string; shift: number; frames: readonly number[]; duration: number; start: number }

type Props = {
  keys: readonly string[]
  activeKey: string | null
  enabled: boolean
  renderPage: (key: string) => ReactNode
  onNavigate: (key: string) => void
  onPrepare?: (key: string) => void
  preview?: RefObject<SwipePreview | null>
  children: ReactNode
}

const SLOP = 10
const LOCK_RATIO = 2
const EDGE = 20
const BLOCKED = '[data-no-swipe], input, textarea, select, [contenteditable="true"]'

const decide = (dx: number, dy: number): Gesture['lock'] => {
  if (Math.hypot(dx, dy) < SLOP) return 'none'
  return Math.abs(dx) >= Math.abs(dy) * LOCK_RATIO ? 'x' : 'y'
}

const translate = (x: number) => `translate3d(${x}px, 0, 0)`

const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

const scrollsX = (el: Element | null, root: Element) => {
  for (let node = el; node && node !== root; node = node.parentElement) {
    const { overflowX } = getComputedStyle(node)
    if ((overflowX === 'auto' || overflowX === 'scroll') && node.scrollWidth > node.clientWidth) return true
  }
  return false
}

const Page = memo(function Page({ id, render }: { id: string; render: RefObject<(key: string) => ReactNode> }) {
  return <Suspense fallback={null}>{render.current(id)}</Suspense>
})

const now = () => {
  const t = document.timeline.currentTime
  return typeof t === 'number' ? t : performance.now()
}

const currentX = (track: HTMLElement) => new DOMMatrixReadOnly(getComputedStyle(track).transform).m41

export function SwipePager({ keys, activeKey, enabled, renderPage, onNavigate, onPrepare, preview, children }: Props) {
  const rootRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const [peers, setPeers] = useState<readonly Peer[]>([])
  const commit = useRef<Commit | null>(null)
  const settle = useRef<((c: Commit) => void) | null>(null)
  const live = useRef({ keys, activeKey, onNavigate, preview })
  const render = useRef(renderPage)
  const paged = !!activeKey && keys.includes(activeKey)

  useLayoutEffect(() => {
    live.current = { keys, activeKey, onNavigate, preview }
    render.current = renderPage
  })

  useEffect(() => {
    const root = rootRef.current
    const track = trackRef.current
    if (!root || !track || !enabled) return undefined
    let gesture: Gesture | null = null
    let anim: Animation | null = null
    let frame = 0
    let swallowClick = false
    const shown = new Set<string>()

    const finish = () => {
      track.style.transform = ''
      track.style.willChange = ''
      root.classList.remove(styles.moving ?? '')
      shown.clear()
      setPeers([])
    }

    const play = (frames: readonly number[], duration: number, done: (() => void) | null, start: number) => {
      anim?.cancel()
      anim = null
      track.style.transform = translate(frames.at(-1) ?? 0)
      if (reduced() || now() - start >= duration) {
        done?.()
        return
      }
      const run = track.animate(
        frames.map((x) => ({ transform: translate(x) })),
        { duration, easing: 'linear' },
      )
      run.startTime = start
      run.onfinish = () => {
        if (anim === run) anim = null
        done?.()
      }
      anim = run
    }

    const animate = (from: number, to: number, velocity: number) => {
      const { frames, duration } = springFrames(from, to, velocity)
      play(frames, duration, finish, now())
    }

    const neighbour = (side: 1 | -1) => {
      const { keys: list, activeKey: active } = live.current
      const index = active ? list.indexOf(active) : -1
      return index < 0 ? undefined : list[index + side]
    }

    const showPeer = (key: string, side: 1 | -1) => {
      if (shown.has(key)) return
      shown.add(key)
      const top = window.scrollY
      startTransition(() => setPeers((prev) => (prev.some((p) => p.key === key) ? prev : [...prev, { key, side, top }])))
    }

    const down = (e: PointerEvent) => {
      if (e.pointerType !== 'touch' || !e.isPrimary || gesture) return
      if (e.clientX < EDGE || commit.current) return
      const target = e.target instanceof Element ? e.target : null
      if (!target || !(root.contains(target) || target.contains(root))) return
      if (target.closest(BLOCKED) || scrollsX(target, root)) return
      let base = 0
      if (anim) {
        base = currentX(track)
        anim.cancel()
        anim = null
        track.style.transform = translate(base)
      }
      gesture = { id: e.pointerId, x0: e.clientX, y0: e.clientY, base, lock: 'none', samples: [{ t: e.timeStamp, x: e.clientX }], width: root.offsetWidth, offset: base }
    }

    const lock = (g: Gesture, dx: number, dy: number) => {
      if (g.lock !== 'none') return g.lock
      g.lock = decide(dx, dy)
      if (g.lock === 'x') {
        g.width = root.offsetWidth
        root.setPointerCapture(g.id)
        root.classList.add(styles.moving ?? '')
        track.style.willChange = 'transform'
      }
      return g.lock
    }

    const touchMove = (e: TouchEvent) => {
      const g = gesture
      const t = e.touches[0]
      if (!g || !t || e.touches.length > 1) return
      if (lock(g, t.clientX - g.x0, t.clientY - g.y0) === 'x' && e.cancelable) e.preventDefault()
    }

    const move = (e: PointerEvent) => {
      const g = gesture
      if (!g || e.pointerId !== g.id) return
      const dx = e.clientX - g.x0
      if (lock(g, dx, e.clientY - g.y0) !== 'x') return
      g.samples.push({ t: e.timeStamp, x: e.clientX })
      if (g.samples.length > 8) g.samples.shift()
      const raw = g.base + dx
      const side = raw < 0 ? 1 : -1
      const key = neighbour(side)
      g.offset = key ? Math.max(-g.width, Math.min(g.width, raw)) : rubberBand(raw, g.width)
      const from = live.current.activeKey
      if (key) showPeer(key, side)
      if (key && from) live.current.preview?.current?.move(from, key, Math.abs(g.offset) / g.width)
      if (!frame) {
        frame = requestAnimationFrame(() => {
          frame = 0
          if (gesture) track.style.transform = translate(gesture.offset)
        })
      }
    }

    const up = (e: PointerEvent) => {
      const g = gesture
      if (!g || e.pointerId !== g.id) return
      gesture = null
      cancelAnimationFrame(frame)
      frame = 0
      if (g.lock !== 'x') {
        if (g.base !== 0) animate(g.base, 0, 0)
        return
      }
      swallowClick = true
      window.setTimeout(() => (swallowClick = false), 350)
      const velocity = e.type === 'pointercancel' ? 0 : velocityOf(g.samples)
      const side = g.offset < 0 ? 1 : -1
      const key = neighbour(side)
      const projected = project(g.offset, velocity)
      const go = !!key && Math.sign(projected) === Math.sign(g.offset) && Math.abs(projected) > g.width / 2 && Math.abs(g.offset) > 12
      const { activeKey: from, preview: pv } = live.current
      pv?.current?.end(go)
      if (!go || !key || !from) {
        animate(g.offset, 0, velocity)
        return
      }
      const { frames, duration } = springFrames(g.offset, -side * g.width, velocity)
      const start = now()
      play(frames, duration, null, start)
      commit.current = { to: key, shift: side * g.width, frames, duration, start }
      shown.add(from)
      setPeers((prev) => [...prev.filter((p) => p.key !== from), { key: from, side: side === 1 ? -1 : 1, top: -window.scrollY }])
      live.current.onNavigate(key)
    }

    const click = (e: MouseEvent) => {
      if (!swallowClick) return
      swallowClick = false
      e.preventDefault()
      e.stopPropagation()
    }

    settle.current = (c: Commit) => {
      window.scrollTo(0, 0)
      play(
        c.frames.map((x) => x + c.shift),
        c.duration,
        finish,
        c.start,
      )
    }

    const { body, documentElement: html } = document
    const restore = { touch: body.style.touchAction, overscroll: html.style.overscrollBehaviorX }
    body.style.touchAction = 'pan-y pinch-zoom'
    html.style.overscrollBehaviorX = 'none'
    document.addEventListener('pointerdown', down)
    document.addEventListener('pointermove', move)
    document.addEventListener('touchmove', touchMove, { passive: false })
    document.addEventListener('pointerup', up)
    document.addEventListener('pointercancel', up)
    document.addEventListener('click', click, true)
    return () => {
      body.style.touchAction = restore.touch
      html.style.overscrollBehaviorX = restore.overscroll
      document.removeEventListener('pointerdown', down)
      document.removeEventListener('pointermove', move)
      document.removeEventListener('touchmove', touchMove)
      document.removeEventListener('pointerup', up)
      document.removeEventListener('pointercancel', up)
      document.removeEventListener('click', click, true)
      cancelAnimationFrame(frame)
      anim?.cancel()
      settle.current = null
    }
  }, [paged, enabled])

  useLayoutEffect(() => {
    const c = commit.current
    if (!c || c.to !== activeKey) return
    commit.current = null
    settle.current?.(c)
  }, [activeKey])

  useEffect(() => {
    if (!onPrepare || !activeKey) return undefined
    const index = keys.indexOf(activeKey)
    const id = window.setTimeout(() => {
      for (const key of [keys[index - 1], keys[index + 1]]) if (key) onPrepare(key)
    }, 300)
    return () => window.clearTimeout(id)
  }, [activeKey, keys, onPrepare])

  if (!paged) return children

  return (
    <div ref={rootRef} className={styles.pager}>
      <div ref={trackRef} className={styles.track}>
        {keys.map((key) => {
          const peer = key === activeKey ? undefined : peers.find((p) => p.key === key)
          if (key !== activeKey && !peer) return null
          return (
            <div
              key={key}
              className={peer ? styles.peer : undefined}
              style={peer ? { top: peer.top, transform: `translateX(calc(${peer.side} * (100% + 2 * var(--pager-gutter))))` } : undefined}
              inert={peer ? true : undefined}
            >
              <Page id={key} render={render} />
            </div>
          )
        })}
      </div>
    </div>
  )
}
